import * as React from 'react';

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/dialog';
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger } from '../components/drawer';
import { useScreenSize } from '../hooks/use-screen-size';
import { cn } from '../lib/utils';

type BaseProps = {
    children: React.ReactNode;
};

type RootPopoutProps = {
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
} & BaseProps;

type PopoutProps = {
    className?: string;
    asChild?: true;
} & BaseProps;

function PopoutRoot({ children, ...props }: RootPopoutProps) {
    const { isMobile } = useScreenSize();
    const Container = isMobile ? Drawer : Dialog;

    return <Container {...props}>{children}</Container>;
}

PopoutRoot.displayName = 'PopoutRoot';

function PopoutTrigger({ className, children, ...props }: PopoutProps) {
    const { isMobile } = useScreenSize();
    const Trigger = isMobile ? DrawerTrigger : DialogTrigger;

    return (
        <Trigger className={className} {...props}>
            {children}
        </Trigger>
    );
}

PopoutRoot.Trigger = PopoutTrigger;
PopoutTrigger.displayName = 'PopoutTrigger';

function PopoutClose({ className, children, ...props }: PopoutProps) {
    const { isMobile } = useScreenSize();
    const Close = isMobile ? DrawerClose : DialogClose;

    return (
        <Close className={className} {...props}>
            {children}
        </Close>
    );
}

PopoutRoot.Close = PopoutClose;
PopoutClose.displayName = 'PopoutClose';

function PopoutContent({ className, children, ...props }: PopoutProps) {
    const { isMobile } = useScreenSize();
    const Content = isMobile ? DrawerContent : DialogContent;

    return (
        <Content className={className} {...props}>
            {children}
        </Content>
    );
}

PopoutRoot.Content = PopoutContent;
PopoutContent.displayName = 'PopoutContent';

function PopoutDescription({ className, children, ...props }: PopoutProps) {
    const { isMobile } = useScreenSize();
    const Description = isMobile ? DrawerDescription : DialogDescription;

    return (
        <Description className={className} {...props}>
            {children}
        </Description>
    );
}

PopoutRoot.Description = PopoutDescription;
PopoutDescription.displayName = 'PopoutDescription';

function PopoutHeader({ className, children, ...props }: PopoutProps) {
    const { isMobile } = useScreenSize();
    const Header = isMobile ? DrawerHeader : DialogHeader;

    return (
        <Header
            className={cn('flex-row items-center justify-center', className)}
            {...props}
        >
            {children}
        </Header>
    );
}

PopoutRoot.Header = PopoutHeader;
PopoutHeader.displayName = 'PopoutHeader';

function PopoutTitle({ className, children, ...props }: PopoutProps) {
    const { isMobile } = useScreenSize();
    const Title = isMobile ? DrawerTitle : DialogTitle;

    return (
        <Title className={cn('font-semibold text-xl', className)} {...props}>
            {children}
        </Title>
    );
}

PopoutRoot.Title = PopoutTitle;
PopoutTitle.displayName = 'PopoutTitle';

function PopoutBody({ className, children, ...props }: PopoutProps) {
    const { size } = useScreenSize();

    return (
        <div
            className={cn(
                'flex flex-col gap-2 overflow-auto px-6',
                {
                    'py-4': size !== 'mobile',
                    'py-2': size === 'mobile',
                },
                className,
            )}
            {...props}
        >
            {children}
        </div>
    );
}

PopoutRoot.Body = PopoutBody;
PopoutBody.displayName = 'PopoutBody';

function PopoutFooter({ className, children, ...props }: PopoutProps) {
    const { isMobile } = useScreenSize();
    const Footer = isMobile ? DrawerFooter : DialogFooter;

    return (
        <Footer className={className} {...props}>
            {children}
        </Footer>
    );
}

PopoutRoot.Footer = PopoutFooter;
PopoutFooter.displayName = 'PopoutFooter';

const Popout = Object.assign(PopoutRoot, {
    Trigger: PopoutTrigger,
    Close: PopoutClose,
    Content: PopoutContent,
    Description: PopoutDescription,
    Header: PopoutHeader,
    Title: PopoutTitle,
    Body: PopoutBody,
    Footer: PopoutFooter,
});

export {
    Popout,
    PopoutBody,
    PopoutClose,
    PopoutContent,
    PopoutDescription,
    PopoutFooter,
    PopoutHeader,
    PopoutRoot,
    PopoutTitle,
    PopoutTrigger,
};
